import apiClient from "utils/apiClient";
import { ACTOR_ID_HEADER_KEY, OLREADIA_SERVICE_BASE_URL } from "constants";

const API_V1 = `${OLREADIA_SERVICE_BASE_URL}/api/v1`;

const unwrap = (response) => response.data;

const withPagination = (params = {}) => {
  const { page = 0, size = 20, ...rest } = params;
  return { page, size, ...rest };
};

const withActor = (actorId) =>
  actorId ? { headers: { [ACTOR_ID_HEADER_KEY]: actorId } } : {};

const normalizeBusinessEntityPayload = (data) => {
  if (!data || typeof data !== "object") return data;

  const businessLocationTypeId =
    data.businessLocationTypeId ||
    data.businessLocationType?.id ||
    data.businessLocationType ||
    null;
  const businessLocationTypeCode = data.businessLocationType?.code || null;

  return {
    ...data,
    ...(businessLocationTypeId ? { businessLocationTypeId } : {}),
    ...(businessLocationTypeCode ? { businessLocationTypeCode } : {}),
  };
};

const normalizeBusinessLocationPayload = (data) => {
  if (!data || typeof data !== "object") return data;

  const businessLocationTypeId =
    data.businessLocationTypeId ||
    data.businessLocationType?.id ||
    data.businessLocationType ||
    null;
  const businessLocationTypeCode = data.businessLocationType?.code || null;

  return {
    ...data,
    ...(businessLocationTypeId ? { businessLocationTypeId } : {}),
    ...(businessLocationTypeCode ? { businessLocationTypeCode } : {}),
  };
};

export const olreadiaService = {
  backgroundChecks: {
    listForProfessionProfile: async (professionProfileId, checkType) =>
      apiClient
        .get(`${API_V1}/background-checks/profession-profiles/${professionProfileId}`, {
          params: checkType ? { checkType } : undefined,
        })
        .then(unwrap),

    get: async (backgroundCheckId) =>
      apiClient.get(`${API_V1}/background-checks/${backgroundCheckId}`).then(unwrap),

    requestForProfessionProfile: async (professionProfileId, data, actorId) =>
      apiClient
        .post(`${API_V1}/background-checks/profession-profiles/${professionProfileId}/request`, data, withActor(actorId))
        .then(unwrap),

    initiateForProfessionProfile: async (professionProfileId, data, actorId) =>
      apiClient
        .post(`${API_V1}/background-checks/profession-profiles/${professionProfileId}/initiate`, data, withActor(actorId))
        .then(unwrap),

    createForProfessionProfile: async (professionProfileId, data, actorId) =>
      apiClient
        .post(`${API_V1}/background-checks/profession-profiles/${professionProfileId}`, data, withActor(actorId))
        .then(unwrap),

    update: async (backgroundCheckId, data, actorId) =>
      apiClient
        .put(`${API_V1}/background-checks/${backgroundCheckId}`, data, withActor(actorId))
        .then(unwrap),

    initiate: async (backgroundCheckId, actorId) =>
      apiClient
        .post(`${API_V1}/background-checks/${backgroundCheckId}/initiate`, null, withActor(actorId))
        .then(unwrap),

    cancel: async (backgroundCheckId, actorId) =>
      apiClient
        .put(`${API_V1}/background-checks/${backgroundCheckId}/cancel`, null, withActor(actorId))
        .then(unwrap),

    submitItemResult: async (backgroundCheckId, data, actorId) =>
      apiClient
        .patch(`${API_V1}/background-checks/${backgroundCheckId}/check`, data, withActor(actorId))
        .then(unwrap),
  },

  helperProfiles: {
    list: async (params = {}) =>
      apiClient
        .get(`${API_V1}/helper-profiles`, { params })
        .then(unwrap),

    get: async (userId) =>
      apiClient.get(`${API_V1}/helper-profiles/${userId}`).then(unwrap),

    updateStatus: async (userId, data) =>
      apiClient.put(`${API_V1}/helper-profiles/${userId}/status`, data).then(unwrap),
  },

  professionProfiles: {
    list: async (params = {}) =>
      apiClient
        .get(`${API_V1}/profession-profiles`, { params: withPagination(params) })
        .then(unwrap),

    get: async (professionProfileId) =>
      apiClient.get(`${API_V1}/profession-profiles/${professionProfileId}`).then(unwrap),

    listByUser: async (userId, params = {}) =>
      apiClient
        .get(`${API_V1}/profession-profiles`, {
          params: { ...withPagination(params), userId: userId || undefined },
        })
        .then(unwrap),

    listForUser: async (userId, params = {}) =>
      olreadiaService.professionProfiles.listByUser(userId, params),

    listRequirements: async (params = {}) =>
      apiClient
        .get(`${API_V1}/profession-profiles/requirements`, { params })
        .then(unwrap),

    createForUser: async (userId, data) =>
      apiClient
        .post(`${API_V1}/profession-profiles`, data, { params: { userId: userId || undefined } })
        .then(unwrap),

    updateForUser: async (userId, data) =>
      apiClient
        .put(`${API_V1}/profession-profiles`, data, { params: { userId: userId || undefined } })
        .then(unwrap),

    delete: async (professionProfileId, params = {}) =>
      apiClient
        .delete(`${API_V1}/profession-profiles/${professionProfileId}`, { params })
        .then(unwrap),
  },

  businessEntities: {
    list: async (params = {}) =>
      apiClient
        .get(`${API_V1}/business-entities`, { params: withPagination(params) })
        .then(unwrap),

    get: async (businessEntityId) =>
      apiClient.get(`${API_V1}/business-entities/${businessEntityId}`).then(unwrap),

    create: async (data, actorId) =>
      apiClient
        .post(
          `${API_V1}/business-entities`,
          normalizeBusinessEntityPayload(data),
          withActor(actorId),
        )
        .then(unwrap),

    update: async (businessEntityId, data, actorId) =>
      apiClient
        .patch(
          `${API_V1}/business-entities/${businessEntityId}`,
          normalizeBusinessEntityPayload(data),
          withActor(actorId),
        )
        .then(unwrap),

    delete: async (businessEntityId, actorId) =>
      apiClient
        .delete(`${API_V1}/business-entities/${businessEntityId}`, withActor(actorId))
        .then(unwrap),

    users: {
      list: async (businessEntityId, params = {}) =>
        apiClient
          .get(`${API_V1}/business-entities/${businessEntityId}/users`, {
            params: withPagination(params),
          })
          .then(unwrap),

      add: async (businessEntityId, data, actorId) =>
        apiClient
          .post(`${API_V1}/business-entities/${businessEntityId}/users`, data, withActor(actorId))
          .then(unwrap),

      update: async (businessEntityId, userId, data, actorId) =>
        apiClient
          .patch(
            `${API_V1}/business-entities/${businessEntityId}/users/${userId}`,
            data,
            withActor(actorId),
          )
          .then(unwrap),

      remove: async (businessEntityId, userId, actorId) =>
        apiClient
          .delete(
            `${API_V1}/business-entities/${businessEntityId}/users/${userId}`,
            withActor(actorId),
          )
          .then(unwrap),
    },

    locations: {
      list: async (businessEntityId, params = {}) =>
        apiClient
          .get(`${API_V1}/business-entities/${businessEntityId}/business-locations`, {
            params: withPagination(params),
          })
          .then(unwrap),

      get: async (businessEntityId, businessLocationId) =>
        apiClient
          .get(
            `${API_V1}/business-entities/${businessEntityId}/business-locations/${businessLocationId}`,
          )
          .then(unwrap),

      create: async (businessEntityId, data, actorId) =>
        apiClient
          .post(
            `${API_V1}/business-entities/${businessEntityId}/business-locations`,
            normalizeBusinessLocationPayload(data),
            withActor(actorId),
          )
          .then(unwrap),

      update: async (businessEntityId, businessLocationId, data, actorId) =>
        apiClient
          .patch(
            `${API_V1}/business-entities/${businessEntityId}/business-locations/${businessLocationId}`,
            normalizeBusinessLocationPayload(data),
            withActor(actorId),
          )
          .then(unwrap),

      delete: async (businessEntityId, businessLocationId, actorId) =>
        apiClient
          .delete(
            `${API_V1}/business-entities/${businessEntityId}/business-locations/${businessLocationId}`,
            withActor(actorId),
          )
          .then(unwrap),
    },
  },

  businessLocationTypes: {
    list: async (params = {}) =>
      apiClient
        .get(`${API_V1}/business-location-types`, {
          params: withPagination({ size: 50, ...params }),
        })
        .then(unwrap),
  },

  serviceOfferings: {
    list: async (params = {}) =>
      apiClient
        .get(`${API_V1}/service-offerings`, { params: withPagination(params) })
        .then(unwrap),

    get: async (serviceId) =>
      apiClient.get(`${API_V1}/service-offerings/${serviceId}`).then(unwrap),

    create: async (data, actorId) =>
      apiClient.post(`${API_V1}/service-offerings`, data, withActor(actorId)).then(unwrap),

    update: async (serviceId, data, actorId) =>
      apiClient
        .patch(`${API_V1}/service-offerings/${serviceId}`, data, withActor(actorId))
        .then(unwrap),

    delete: async (serviceId, actorId) =>
      apiClient.delete(`${API_V1}/service-offerings/${serviceId}`, withActor(actorId)).then(unwrap),

    businessEntityServices: {
      list: async (businessEntityId) =>
        apiClient.get(`${API_V1}/service-offerings/business-entities/${businessEntityId}`).then(unwrap),

      add: async (businessEntityId, serviceId, actorId) =>
        apiClient
          .post(
            `${API_V1}/service-offerings/business-entities/${businessEntityId}/business-offerings/${serviceId}`,
            null,
            withActor(actorId),
          )
          .then(unwrap),

      remove: async (businessEntityId, serviceId, actorId) =>
        apiClient
          .delete(
            `${API_V1}/service-offerings/business-entities/${businessEntityId}/business-offerings/${serviceId}`,
            withActor(actorId),
          )
          .then(unwrap),
    },

    businessLocationServices: {
      list: async (businessEntityId, businessLocationId) =>
        apiClient
          .get(
            `${API_V1}/service-offerings/business-entities/${businessEntityId}/business-locations/${businessLocationId}`,
          )
          .then(unwrap),

      add: async (businessEntityId, businessLocationId, serviceId, actorId) =>
        apiClient
          .post(
            `${API_V1}/service-offerings/business-entities/${businessEntityId}/business-locations/${businessLocationId}/business-offerings/${serviceId}`,
            null,
            withActor(actorId),
          )
          .then(unwrap),

      remove: async (businessEntityId, businessLocationId, serviceId, actorId) =>
        apiClient
          .delete(
            `${API_V1}/service-offerings/business-entities/${businessEntityId}/business-locations/${businessLocationId}/business-offerings/${serviceId}`,
            withActor(actorId),
          )
          .then(unwrap),
    },
  },
};

export default olreadiaService;
